import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from 'react';
import { useUnit } from 'effector-react';
import { combine } from 'effector';
import cx from 'classnames';

import {
  $sceneOpenIntros,
  type SceneOpenIntroPayload,
} from '../../state/sceneOpen';
import { $sceneId } from '../../state/appState';
import { $locale } from '../../state/locale';
import { LOCALE_TEXT_DIRECTION } from '../../shared/locale';
import {
  $activeSceneId,
  enterScene,
  resetNavigation,
} from '../../state/sceneNavigation';
import { SCENE_POI_ID } from '../../scene/sceneNavigationMap';
import {
  getSceneBannerKind,
  resolveBannerLocationKey,
  resolveMainBannerSceneUrlId,
  SCENE_BANNER_BUTTON_LABEL_KEYS,
  type SceneBannerKind,
} from '../../scene/sceneBannerMap';
import { getCanonicalInsideSceneId, isInsideScene } from '../../scene/insideScenes/registry';
import type { SceneConfig } from '../../scene/sceneConfig';
import type { SplatSceneApi, OverviewCameraPose } from '../../scene/splatSceneApi';
import { sceneEntered } from '../../analytics/sceneTracker';
import { sceneBannerClicked } from '../../analytics/navigationTracker';
import { overallSliderSlideChanged } from '../../analytics/overallTracker';
import { useI18n } from '../../i18n';

import { useLayoutMode } from '../../hooks/useLayoutMode';

import { IntroSlideContent } from './IntroSlideContent';
import {
  IntroDesktopLayout,
  type DesktopSlide,
} from './IntroDesktopLayout';
import type { DesktopMenuItem } from './DesktopSideMenu';
import { PaginationDots } from './PaginationDots';
import { useSlideSwipe } from './useSlideSwipe';
import { parseOverallIntro } from './parseIntro';
import styles from './OverallSceneOverlay.module.css';

/** Time the slot crossfade runs (ms). Match the LONGEST point in the
 *  CSS pipeline: `slot-enter` is delayed 140 ms then runs 400 ms = 540 ms
 *  total. Bumped a few frames over so React drops the old slot AFTER
 *  the entering slot has fully settled; input lock (`busy`) releases at
 *  the same point. */
const SLIDE_TRANSITION_MS = 560;

/**
 * Intro for the active scene from the scene_open reply cache. Keyed by
 * `$sceneId` (poiFilterId) — `'overall-scene'` for the root scene.
 * Module-local: only this overlay consumes it.
 */
const $currentSceneOpenIntro = combine(
  $sceneOpenIntros,
  $sceneId,
  (map, sceneId): SceneOpenIntroPayload | null => {
    if (sceneId === null) return null;
    return map.get(String(sceneId)) ?? null;
  },
);

type Slide = {
  /** Stable React key — `'welcome'` or the backend banner image id. */
  key: string;
  kind: 'welcome' | 'scene';
  title: string | null;
  description: string | null;
  /** CTA label for scene slides ("Explore Kaaba"). `null` on welcome. */
  ctaLabel: string | null;
  /** Backend scene id to enter on CTA click ('main' / 'arafat-scene' / 'kaaba-inside' / …). */
  targetSceneId: string | null;
  /** Used for analytics — `'main'` / `'inside'`. `null` on welcome. */
  bannerKind: SceneBannerKind | null;
  /** Pose to fly camera to on slide activation (mobile only — desktop
   *  shows multiple cards at once and doesn't drive camera). */
  cameraPose: OverviewCameraPose | null;
};

type Props = {
  splatApi: SplatSceneApi | null;
  splatConfig: SceneConfig;
  /** When true, the overlay is hidden via display:none (preserves CSS-vars
   *  and React state — used to "tuck away" while PoiArtifact overlay
   *  covers the scene). */
  hidden?: boolean;
};

/**
 * Root-scene intro overlay (`activeSceneId === 'overall'`). Replaces the
 * chat panel with a bottom gradient curtain containing:
 *
 *   - one active slide at a time (welcome + per-scene cards)
 *   - pagination dots with dynamic distance-from-active sizing
 *
 * Slides advance via swipe (horizontal pointer drag), dot click, or — on
 * scene slides — the "Explore X" CTA button. Camera flies to the slide's
 * overview pose on every change. The CTA fires the cinematic-+-scene-
 * change flow (same as a 3D nav-POI marker click) via `splatApi.navigateToScene`.
 *
 * Animation strategy: two-slot crossfade. The previous slide stays mounted
 * for `SLIDE_TRANSITION_MS` while CSS animates it out (`.slotLeave`),
 * while the new slide simultaneously animates in (`.slotEnter`). After the
 * window elapses, the previous slot is unmounted. The swipe hook + dot
 * clicks are gated `busy` until idle so rapid input doesn't interleave
 * partial animations.
 *
 * See `docs/architecture/overlays.md` for the parent-overlay rules (CSS-var cleanup,
 * pointer-events handoff to canvas, etc.).
 */
export const OverallSceneOverlay: FC<Props> = ({ splatApi, splatConfig, hidden }) => {
  const { intro, activeSceneId, locale } = useUnit({
    intro: $currentSceneOpenIntro,
    activeSceneId: $activeSceneId,
    locale: $locale,
  });
  const i18n = useI18n();
  const isRtl = LOCALE_TEXT_DIRECTION[locale] === 'rtl';
  const layoutMode = useLayoutMode();
  const isDesktop = layoutMode === 'desktop';
  const navTransitionPendingRef = useRef(false);

  // Keep `splatApi` in a ref so handlers always read the latest instance
  // without re-binding swipe / click callbacks on every parent render.
  const splatApiRef = useRef(splatApi);
  useEffect(() => {
    splatApiRef.current = splatApi;
  }, [splatApi]);

  // ── Parse intro + build slides ───────────────────────────────────────
  const { welcomeTitle, welcomeDescription, banners } = useMemo(
    () => parseOverallIntro(intro),
    [intro],
  );

  // Memoised camera-pose lookup. Welcome → where the preloader left the camera:
  // `splatApi.restPose` (the scene's routes), else previewTourEnd (or
  // fallback to initialPosition / orbitCenter). Scene →
  // `navigationPois[*].overviewCameraPosition`.
  const poseLookup = useMemo(() => {
    const map = new Map<string, OverviewCameraPose>();
    const tourEnd = splatApi?.restPose ?? splatConfig.camera.previewTourEnd;
    const welcomePosition = tourEnd?.position ?? splatConfig.camera.initialPosition;
    const welcomeTarget = tourEnd?.target
      ?? splatConfig.controls?.orbitCenter
      ?? { x: 0, y: 0, z: 0 };
    if (welcomePosition) {
      map.set('__welcome__', { position: welcomePosition, target: welcomeTarget });
    }
    const orbitCenter = splatConfig.controls?.orbitCenter ?? { x: 0, y: 0, z: 0 };
    for (const poi of splatConfig.navigationPois ?? []) {
      if (!poi.overviewCameraPosition) continue;
      map.set(poi.targetSceneId, {
        position: poi.overviewCameraPosition,
        target: poi.overviewCameraTarget ?? orbitCenter,
      });
    }
    return map;
  }, [splatConfig, splatApi]);

  const slides = useMemo<Slide[]>(() => {
    const result: Slide[] = [];

    if (welcomeTitle || welcomeDescription) {
      result.push({
        key: 'welcome',
        kind: 'welcome',
        title: welcomeTitle,
        description: welcomeDescription,
        ctaLabel: null,
        targetSceneId: null,
        bannerKind: null,
        cameraPose: poseLookup.get('__welcome__') ?? null,
      });
    }

    for (const banner of banners) {
      const sceneId = banner.moveToSceneId;
      if (!sceneId) continue;
      const kind = getSceneBannerKind(sceneId);
      if (!kind || kind === 'artifact') continue;

      const locationKey = resolveBannerLocationKey(kind, sceneId);
      // Long-form display name for menu + slide title — pulls from
      // `overallOverlay.sceneLabels` ("Arafat Mount" / "Mina Valley")
      // first, falling back to the short `markerLabels` if no long form
      // is defined. Used for the slide title and the CTA template's
      // `{name}` slot, so the visible name is consistent across all
      // surfaces inside the overlay.
      const sceneLabels = i18n.overallOverlay.sceneLabels as Record<string, string>;
      const longName = locationKey ? sceneLabels[locationKey] : '';
      const shortName = locationKey
        ? i18n.globalMap.markerLabels[locationKey] ?? ''
        : '';
      const displayName = longName || shortName;
      const title = displayName || banner.title || '';
      const ctaLabelTemplate = i18n.assistant[SCENE_BANNER_BUTTON_LABEL_KEYS[kind]];
      const ctaLabel = ctaLabelTemplate.replace('{name}', displayName);
      const urlId = kind === 'main'
        ? resolveMainBannerSceneUrlId(sceneId) ?? sceneId
        : sceneId;

      result.push({
        key: banner.id,
        kind: 'scene',
        title,
        description: banner.description ?? null,
        ctaLabel,
        targetSceneId: sceneId,
        bannerKind: kind,
        cameraPose: poseLookup.get(urlId) ?? null,
      });
    }
    return result;
  }, [welcomeTitle, welcomeDescription, banners, poseLookup, i18n]);

  // Desktop side-menu items — one per slide in slide-order. Welcome →
  // `i18n.overallOverlay.mapLabel` ("Map" / "خريطة"); scene → long-form
  // `sceneLabels` ("Mina Valley" / "Arafat Mount" — design 22302/22318).
  // Falls back to `markerLabels` short form, then `slide.title` from
  // backend, then `slide.key` as last resort.
  const desktopMenuItems = useMemo<DesktopMenuItem[]>(() => {
    const sceneLabels = i18n.overallOverlay.sceneLabels as Record<string, string>;
    return slides.map((slide) => {
      if (slide.kind === 'welcome') {
        return { key: slide.key, label: i18n.overallOverlay.mapLabel };
      }
      const locationKey = slide.targetSceneId
        ? resolveBannerLocationKey(slide.bannerKind ?? 'main', slide.targetSceneId)
        : null;
      const label = locationKey
        ? sceneLabels[locationKey]
          ?? i18n.globalMap.markerLabels[locationKey]
          ?? slide.title
          ?? slide.key
        : slide.title ?? slide.key;
      return { key: slide.key, label };
    });
  }, [slides, i18n]);

  // ── Active slide + animation slots ───────────────────────────────────
  const [activeIndex, setActiveIndex] = useState(0);

  // Two-slot crossfade. `current` is the active slide, `previous` is the
  // outgoing slide that stays mounted until SLIDE_TRANSITION_MS elapses.
  const [current, setCurrent] = useState<Slide | null>(slides[0] ?? null);
  const [previous, setPrevious] = useState<Slide | null>(null);
  const [busy, setBusy] = useState(false);

  // Reset to slide 0 when the slide set itself changes (new intro
  // received). Identity-compare via the joined keys — different content
  // count or different banner ids both flip this.
  const slideSignature = slides.map((s) => s.key).join('|');
  useEffect(() => {
    setActiveIndex(0);
    setCurrent(slides[0] ?? null);
    setPrevious(null);
    setBusy(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slideSignature]);

  // Drive the slot transition when activeIndex changes. `current` always
  // tracks `slides[activeIndex]`; `previous` is filled with the outgoing
  // slide and cleared after SLIDE_TRANSITION_MS.
  useEffect(() => {
    const target = slides[activeIndex] ?? null;
    if (!target) {
      setCurrent(null);
      setPrevious(null);
      return;
    }
    if (current?.key === target.key) return;
    setPrevious(current);
    setCurrent(target);
    setBusy(true);
    const t = setTimeout(() => {
      setPrevious(null);
      setBusy(false);
    }, SLIDE_TRANSITION_MS);
    return () => clearTimeout(t);
    // current is intentionally read at-time-of-effect; no need to re-run
    // on every render. We trigger only when activeIndex / slides change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, slideSignature]);

  // ── Side-effects on slide change: camera fly-to + analytics ──────────
  const previousSlideTrackingRef = useRef<{
    key: string;
    index: number;
    activatedAt: number;
  } | null>(null);

  useEffect(() => {
    if (slides.length > 0 && previousSlideTrackingRef.current === null) {
      previousSlideTrackingRef.current = {
        key: slides[0].key,
        index: 0,
        activatedAt: performance.now(),
      };
    }
  }, [slides]);

  const driveSideEffects = useCallback(
    (slide: Slide, index: number) => {
      // Camera fly-to (mobile-style — one slide visible at a time, swipe
      // carries an unambiguous "selected scene" signal).
      const api = splatApiRef.current;
      const triggeredCameraFocus = api !== null && slide.cameraPose !== null;
      if (triggeredCameraFocus && api && slide.cameraPose) {
        api.transitionToPose(slide.cameraPose);
      }

      // Analytics — fires regardless of camera focus so dwell time always
      // gets recorded.
      const now = performance.now();
      const prev = previousSlideTrackingRef.current;
      overallSliderSlideChanged({
        fromKey: prev?.key ?? null,
        toKey: slide.key === 'welcome' ? 'welcome' : (slide.targetSceneId ?? slide.key),
        fromIndex: prev?.index ?? -1,
        toIndex: index,
        dwellOnPreviousMs: prev ? Math.round(now - prev.activatedAt) : 0,
        triggeredCameraFocus,
      });
      previousSlideTrackingRef.current = {
        key: slide.key,
        index,
        activatedAt: now,
      };
    },
    [],
  );

  // Fire side-effects whenever activeIndex changes to a real slide. Using
  // the same activeIndex source-of-truth as the slot transition guarantees
  // they stay in sync.
  useEffect(() => {
    const slide = slides[activeIndex];
    if (!slide) return;
    driveSideEffects(slide, activeIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, slideSignature]);

  // ── User input: swipe + dot click + CTA ──────────────────────────────
  const handleSwipe = useCallback(
    (direction: 'prev' | 'next') => {
      if (busy) return;
      setActiveIndex((idx) => {
        if (direction === 'next' && idx < slides.length - 1) return idx + 1;
        if (direction === 'prev' && idx > 0) return idx - 1;
        return idx;
      });
    },
    [busy, slides.length],
  );

  const swipeHandlers = useSlideSwipe({
    onSwipe: handleSwipe,
    // Disabled on desktop — keyboard arrows + menu clicks are the only
    // navigation there. Also disabled while a transition is in flight
    // or there's nothing to navigate to.
    disabled: busy || slides.length <= 1 || isDesktop,
    rtl: isRtl,
  });

  // Desktop keyboard navigation: window-level ←/→ handler. RTL swaps the
  // semantics (right arrow = previous, left arrow = next). Listener is
  // active only on desktop layout; mobile uses swipe.
  //
  // Registered in CAPTURE phase with `stopPropagation()`, so the slider owns
  // the keypress on overall: no later listener (the engine's arrow-key orbit,
  // say) reacts to it as well.
  useEffect(() => {
    if (!isDesktop) return;
    const handler = (e: KeyboardEvent) => {
      // Ignore when the user is typing in an input / textarea / contentEditable.
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const physicalNext = e.key === 'ArrowRight';
      const logicalNext = isRtl ? !physicalNext : physicalNext;
      handleSwipe(logicalNext ? 'next' : 'prev');
      e.stopPropagation();
      e.preventDefault();
    };
    window.addEventListener('keydown', handler, { capture: true });
    return () => window.removeEventListener('keydown', handler, { capture: true });
  }, [isDesktop, isRtl, handleSwipe]);

  const handleDotSelect = useCallback(
    (index: number) => {
      if (busy) return;
      if (index < 0 || index >= slides.length) return;
      setActiveIndex(index);
    },
    [busy, slides.length],
  );

  const handleCtaClick = useCallback(() => {
    if (navTransitionPendingRef.current) return;
    const slide = slides[activeIndex];
    if (!slide || slide.kind !== 'scene') return;
    const sceneId = slide.targetSceneId;
    const kind = slide.bannerKind;
    if (!sceneId || !kind) return;

    if (kind === 'inside') {
      const insideId = getCanonicalInsideSceneId(sceneId);
      if (activeSceneId === insideId) return;
      sceneBannerClicked({
        surface: 'overall-overlay',
        bannerKind: 'inside',
        targetSceneId: insideId,
      });
      navTransitionPendingRef.current = true;
      enterScene({ sceneId: insideId, poiFilterId: SCENE_POI_ID[insideId] ?? insideId });
      sceneEntered(insideId);
      return;
    }
    const urlId = resolveMainBannerSceneUrlId(sceneId);
    if (!urlId) return;
    if (activeSceneId === urlId) return;
    sceneBannerClicked({
      surface: 'overall-overlay',
      bannerKind: 'main',
      targetSceneId: urlId,
    });
    const api = splatApiRef.current;
    if (!api) {
      navTransitionPendingRef.current = true;
      resetNavigation();
      enterScene({
        sceneId: urlId,
        poiFilterId: SCENE_POI_ID[urlId] ?? urlId,
      });
      sceneEntered(urlId);
      return;
    }
    navTransitionPendingRef.current = true;
    api.navigateToScene(urlId);
  }, [slides, activeIndex, activeSceneId]);

  // Reset the multi-click guard whenever active scene changes (defensive —
  // normally the overlay unmounts on transition, but if upstream cancels
  // we still want retries to work).
  useEffect(() => {
    navTransitionPendingRef.current = false;
  }, [activeSceneId]);

  // CSS-var cleanup: canvas hugs the bottom while overlay is mounted.
  // See docs/architecture/overlays.md "Critical rules" #2.
  useEffect(() => {
    const root = document.getElementById('app');
    if (!root) return;
    root.style.setProperty('--panel-current-height', '0px');
    root.style.setProperty('--canvas-panel-gap', '0px');
    return () => {
      root.style.removeProperty('--panel-current-height');
      root.style.removeProperty('--canvas-panel-gap');
    };
  }, []);

  if (slides.length === 0) return null;

  if (isDesktop) {
    return (
      <div className={cx(styles.overlay, hidden && styles.hidden)}>
        <div className={cx(styles.curtain, styles.curtainDesktop)}>
          <IntroDesktopLayout
            current={current ? toDesktopSlide(current) : null}
            previous={previous ? toDesktopSlide(previous) : null}
            menuItems={desktopMenuItems}
            activeIndex={activeIndex}
            busy={busy}
            hintArrows={i18n.overallOverlay.keyboardHintArrows}
            hintAction={i18n.overallOverlay.keyboardHintAction}
            onSelect={handleDotSelect}
            onCtaClick={handleCtaClick}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={cx(styles.overlay, hidden && styles.hidden)}>
      <div className={styles.curtain} {...swipeHandlers}>
        <div className={styles.slideStack}>
          {previous && (
            <div
              key={`prev-${previous.key}`}
              className={cx(styles.slot, styles.slotLeave)}
              aria-hidden="true"
            >
              <IntroSlideContent
                kind={previous.kind}
                title={previous.title}
                description={previous.description}
                buttonLabel={previous.ctaLabel}
                onCtaClick={handleCtaClick}
              />
            </div>
          )}
          {current && (
            <div
              key={`curr-${current.key}`}
              className={cx(styles.slot, styles.slotEnter)}
            >
              <IntroSlideContent
                kind={current.kind}
                title={current.title}
                description={current.description}
                buttonLabel={current.ctaLabel}
                onCtaClick={handleCtaClick}
              />
            </div>
          )}
        </div>

        <PaginationDots
          count={slides.length}
          activeIndex={activeIndex}
          onSelect={handleDotSelect}
          busy={busy}
        />
      </div>
    </div>
  );
};

/** Projects an internal `Slide` into the desktop-layout shape (drops
 *  fields the layout doesn't consume). */
function toDesktopSlide(slide: Slide): DesktopSlide {
  return {
    key: slide.key,
    title: slide.title,
    description: slide.description,
    ctaLabel: slide.kind === 'scene' ? slide.ctaLabel : null,
  };
}

OverallSceneOverlay.displayName = 'OverallSceneOverlay';
